// V2, Context-aware progressive (2026-10-04, GUI audit docs/GUI_REDESIGN_AUDIT_2026_10_04.md): the home screen shows the
// widgets that matter for the situation. AUTO picks the situation from real inputs: a critical alert or a dead scheduler
// is an INCIDENT; otherwise the market session decides OPEN or CLOSED. The mode can be forced with the buttons. In every
// mode an unprotected position and any critical alert stay visible (a mode may reorder widgets, never hide a risk).
import React, { useState, useMemo } from 'react'
import SystemAlerts from '../components/SystemAlerts'
import useDeskData from '../lib/useDeskData'
import { Panel, KV, PositionsTable, RiskPanel, EnginePanel, EquityChart, SignalsList, MONO, mainRows, tier2Rows, signed } from '../components/deskParts'
import { RESOLVE_PROMPT, resolveLink } from '../lib/alertGroups'
import { timeAgo } from '../lib/utils'

const MODES = [['auto', 'AUTO'], ['open', 'MARKET OPEN'], ['closed', 'CLOSED / REVIEW'], ['incident', 'INCIDENT']]
const SCOPES = [['MAIN', 'MAIN'], ['TIER2', 'TIER 2'], ['ALL', 'ALL']]

function deadlineText(iso) {
  if (!iso) return ['—', undefined]
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return ['—', undefined]
  if (ms <= 0) return ['OVERDUE', 'bad']
  const h = ms / 3600000
  return [h < 72 ? `${Math.round(h)} h (${new Date(iso).toLocaleString()})` : `${Math.round(h / 24)} d (${new Date(iso).toLocaleDateString()})`, h < 48 ? 'warn' : undefined]
}

export default function ContextDesk() {
  const d = useDeskData({ pollMs: 30_000 })
  const [mode, setMode] = useState('auto')
  const [scope, setScope] = useState('MAIN')
  const [sel, setSel] = useState(null)

  const s = d.main?.summary
  const critical = (d.alerts || []).some(a => a.severity === 'critical') || d.health?.scheduler_alive === false || d.health?.watchdog_alive === false
  const auto = critical ? 'incident' : d.health?.market_session === 'regular' ? 'open' : 'closed'
  const eff = mode === 'auto' ? auto : mode
  const reason = mode !== 'auto' ? 'forced by you'
    : critical ? 'a critical alert or a dead scheduler/watchdog' : `market session is ${d.health?.market_session ?? 'unknown'}`

  const rows = useMemo(() => {
    const m = mainRows(d.main), t = tier2Rows(d.tier2)
    return scope === 'MAIN' ? m : scope === 'TIER2' ? t : [...m, ...t]
  }, [d.main, d.tier2, scope])
  const exceptions = useMemo(() => {
    const m = mainRows(d.main)
    const unprot = m.filter(r => r.has_stop === false)
    const movers = m.filter(r => r.has_stop !== false).sort((a, b) => Math.abs(b.pnl_pct ?? 0) - Math.abs(a.pnl_pct ?? 0)).slice(0, 6)
    return [...unprot, ...movers]
  }, [d.main])
  const [dl, dlTone] = deadlineText(d.schwab?.refresh_token_reauth_deadline)
  const alertCount = (d.alerts || []).filter(a => a.severity === 'critical' || a.severity === 'warning').length   // info rows are not shown anywhere else either

  const positions = (full) => (
    <Panel title={`${scope === 'TIER2' ? 'Tier 2' : scope} positions${full ? '' : ' · exceptions first'}`} bodyStyle={{ padding: 0 }}
      style={{ maxHeight: full ? 420 : 300 }}
      right={scope === 'MAIN' && s ? <span>{s.count} open</span> : null}>
      <PositionsTable rows={full ? rows : (scope === 'MAIN' ? exceptions : scope === 'TIER2' ? rows : [...exceptions, ...tier2Rows(d.tier2)])} selected={sel} onSelect={setSel} showBook={scope === 'ALL'} />
    </Panel>
  )
  const equity = <Panel title={`Equity · ${d.selectedAccount === 'main' ? 'MAIN' : 'TIER 2'}`}><EquityChart data={d.equity} period={d.period} onPeriod={d.setPeriod} height={150} /></Panel>
  const risk = <Panel title="Risk"><RiskPanel account={d.account} main={d.main} tier2={d.tier2} /></Panel>
  const engine = <Panel title="Engine & workers"><EnginePanel health={d.health} ws={d.ws} /></Panel>
  const alertBox = <SystemAlerts health={d.health} schwabStatus={d.schwab} wsStatus={d.ws} dbAlerts={d.alerts} onAckGroup={d.ackGroup} mainSummary={d.main?.summary} />

  let body
  if (eff === 'open') {
    body = (
      <div className="v2-cols-open">
        <div className="flex flex-col gap-2 min-w-0">{positions(true)}{equity}</div>
        <div className="v2-first-sm flex flex-col gap-2 min-w-0">{alertCount > 0 || critical || (s && s.unprotected_count > 0) ? alertBox : null}{risk}<Panel title="Signals" style={{ maxHeight: 260 }} bodyStyle={{ padding: 0 }}><SignalsList signals={d.signals} /></Panel></div>
      </div>
    )
  } else if (eff === 'incident') {
    body = (
      <div className="v2-cols-incident">
        <div className="flex flex-col gap-2 min-w-0">
          {alertBox}
          <Panel title="What RESOLVE does">
            <p style={{ ...MONO, fontSize: '0.7rem', color: '#cbd5e1', lineHeight: 1.6 }}>
              Opens Claude Code on this machine with this prompt typed in. Nothing is sent until you press Enter:
            </p>
            <p style={{ ...MONO, fontSize: '0.7rem', color: '#fcd34d', margin: '6px 0' }}>"{RESOLVE_PROMPT}"</p>
            <a href={resolveLink()} style={{ ...MONO, fontSize: '0.66rem', color: '#67e8f9' }}>open Claude Code</a>
          </Panel>
        </div>
        <div className="flex flex-col gap-2 min-w-0">{positions(false)}{engine}</div>
      </div>
    )
  } else {
    body = (
      <div className="v2-cols-closed">
        <div className="flex flex-col gap-2 min-w-0">
          <Panel title="Before the open">
            <KV rows={[
              ['Schwab browser login', dl, dlTone],
              ['open alerts', alertCount, alertCount > 0 ? 'warn' : 'good'],
              ['unprotected', s ? (s.unprotected_count ? s.unprotected.join(', ') : 'none') : '—', s && s.unprotected_count ? 'bad' : 'good'],
              ['daily bars', d.health?.daily_bars_latest ?? '—', d.health?.daily_bars_days_stale > 1 ? 'warn' : undefined],
              ['stream', d.ws?.status ?? '—'],
            ]} />
          </Panel>
          {alertCount > 0 || (s && s.unprotected_count > 0) ? alertBox : null}
          {positions(false)}
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          {engine}
          {equity}
        </div>
      </div>
    )
  }

  return (
    <div className="tap space-y-2">
      <div className="flex items-center gap-2 flex-wrap" style={{ ...MONO, fontSize: '0.66rem', color: '#64748b' }}>
        <span>V2 · CONTEXT-AWARE</span>
        {MODES.map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)}
            style={{ ...MONO, fontSize: '0.64rem', padding: '2px 8px', borderRadius: 3, border: `1px solid ${mode === k ? '#0e7490' : '#374151'}`, color: mode === k ? '#67e8f9' : '#94a3b8', background: mode === k ? 'rgba(8,145,178,0.12)' : 'transparent' }}>
            {label}
          </button>
        ))}
        <span>showing <b style={{ color: eff === 'incident' ? '#fca5a5' : '#e2e8f0' }}>{eff.toUpperCase()}</b> · {reason}</span>
        <span className="flex-1" />
        <span>scope</span>
        {SCOPES.map(([k, label]) => (
          <button key={k} onClick={() => setScope(k)}
            style={{ ...MONO, fontSize: '0.64rem', padding: '2px 8px', borderRadius: 3, border: `1px solid ${scope === k ? '#0e7490' : '#374151'}`, color: scope === k ? '#67e8f9' : '#94a3b8', background: scope === k ? 'rgba(8,145,178,0.12)' : 'transparent' }}>
            {label}
          </button>
        ))}
        <span>{d.asOf ? `updated ${timeAgo(d.asOf)}` : 'loading…'}{d.errors.length > 0 ? ` · stale: ${d.errors.join(', ')}` : ''}</span>
      </div>
      {body}
    </div>
  )
}
